import React from 'react';
import { Component } from '@/types';
import { Calendar } from '@/components/pm/Calendar';
import { useComponentDataResolver } from '@/services/component-data/useComponentDataResolver';
import type { ComponentDataResolverResult } from '@/services/component-data/componentDataResolver';

export interface SharedCalendarComponentProps {
  component: Component;
  isViewerMode?: boolean;
  inheritedDataResolver?: ComponentDataResolverResult;
}

interface SharedCalendarResolvedProps {
  component: Component;
  isViewerMode: boolean;
  dataResolver: ComponentDataResolverResult;
}

const SharedCalendarResolved: React.FC<SharedCalendarResolvedProps> = ({
  component,
  isViewerMode,
  dataResolver,
}) => {
  // Loading state
  if (dataResolver.loading && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="calendar-loading"
        className="w-full h-full flex items-center justify-center gap-2 text-muted-foreground text-sm"
      >
        <svg
          className="animate-spin"
          style={{ width: 20, height: 20 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading calendar data...
      </div>
    );
  }

  // Error state
  if (dataResolver.error && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="calendar-error"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span className="text-sm text-destructive">{dataResolver.error.message}</span>
        <button
          onClick={() => dataResolver.refetch()}
          style={{
            fontSize: 12,
            color: '#3b82f6',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Calendar
      component={component}
      data={dataResolver.rows}
      isViewerMode={isViewerMode}
      writeTargetPermissions={dataResolver.writeTarget.operations}
      writeTargetRequestingOrganizationId={
        dataResolver.writeTarget.requestingOrganizationId
      }
    />
  );
};

const SharedCalendarStandalone: React.FC<
  Pick<SharedCalendarComponentProps, 'component' | 'isViewerMode'>
> = ({ component, isViewerMode = false }) => {
  const dataResolver = useComponentDataResolver(component);
  return (
    <SharedCalendarResolved
      component={component}
      isViewerMode={isViewerMode}
      dataResolver={dataResolver}
    />
  );
};

export const SharedCalendarComponent: React.FC<SharedCalendarComponentProps> = ({
  component,
  isViewerMode = false,
  inheritedDataResolver,
}) => {
  if (inheritedDataResolver !== undefined) {
    return (
      <SharedCalendarResolved
        component={component}
        isViewerMode={isViewerMode}
        dataResolver={inheritedDataResolver}
      />
    );
  }

  return (
    <SharedCalendarStandalone
      component={component}
      isViewerMode={isViewerMode}
    />
  );
};

SharedCalendarComponent.displayName = 'SharedCalendarComponent';
